import React from 'react';

/*
Shared layout for the bulk "tick a box per service" screens, built so each row is easy to follow
across the page: a scrolling box with a sticky header, striped rows, a highlight on the row under
the mouse, a tinted band for the tick columns, and tick cells that toggle wherever you click in them.
*/

/**
 * @function TickTable
 * @param {JSX.Element} head The <tr> for the header.
 * @param {JSX.Element[]} children The body rows.
 * @return {JSX.Element}
 * @constructor
 */
const TickTable = ({head, children}) => (
    <div className="relative z-0 max-h-[70vh] overflow-auto border border-gray-200 rounded-xl bg-white">
        <table className="w-full text-sm border-separate" style={{borderSpacing: 0}}>
            <thead>{head}</thead>
            <tbody>{children}</tbody>
        </table>
    </div>
);

/**
 * A header cell that stays at the top while the table scrolls.
 *
 * @function TickHeader
 * @param {boolean} tick A tick column (centred, tinted).
 * @param {string} className
 * @param {*} children
 * @return {JSX.Element}
 * @constructor
 */
const TickHeader = ({tick = false, className = '', children}) => (
    <th
        scope="col"
        className={`sticky top-0 z-10 py-3 px-3 border-b-2 border-gray-300 font-bold text-left align-bottom
            ${tick ? 'text-center bg-teal-50 w-36' : 'bg-white'} ${className}`}
    >
        {children}
    </th>
);

/**
 * @function TickRow
 * @param {number} index Used for striping.
 * @param {boolean} changed Unsaved changes on this row.
 * @param {*} children
 * @return {JSX.Element}
 * @constructor
 */
const TickRow = ({index, changed = false, children}) => (
    <tr
        className={`group ${changed ? 'bg-yellow-100' : (index % 2 ? 'bg-gray-50' : 'bg-white')} hover:bg-blue-100`}
        style={changed ? {boxShadow: 'inset 4px 0 0 #F7AF25'} : undefined}
    >
        {children}
    </tr>
);

/**
 * @function TickTextCell
 * @param {string} className
 * @param {*} children
 * @return {JSX.Element}
 * @constructor
 */
const TickTextCell = ({className = '', children}) => (
    <td className={`py-3 px-3 border-b border-gray-200 ${className}`}>{children}</td>
);

/**
 * A tick box that fills its cell, so a click anywhere in the cell toggles it.
 *
 * @function TickCell
 * @param {boolean} checked
 * @param {function} onChange
 * @param {string} label Accessible name, e.g. "Netherfield Fire evacuation: Respite".
 * @param {boolean} disabled
 * @param {string|null} caption Small text under the box.
 * @param {*} children Shown under the tick box, outside its click area (e.g. a Manager toggle).
 * @return {JSX.Element}
 * @constructor
 */
const TickCell = ({checked, onChange, label, disabled = false, caption = null, children = null}) => (
    <td className="p-0 border-b border-l border-gray-200 bg-teal-50 bg-opacity-60 group-hover:bg-blue-100 text-center">
        <label
            className={`flex flex-col items-center justify-center gap-1 w-full h-full py-3 px-2
                ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <input
                type="checkbox"
                className="h-5 w-5"
                aria-label={label}
                checked={checked}
                disabled={disabled}
                onChange={onChange}
            />
            {caption && <span className="text-xs text-gray-600">{caption}</span>}
        </label>
        {children && <div className="flex justify-center pb-2 -mt-1">{children}</div>}
    </td>
);

export {TickTable, TickHeader, TickRow, TickTextCell, TickCell};
